import { useState } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Sparkles, Edit3, Save, X, ChevronDown, ChevronRight, MessageSquare, Send } from 'lucide-react'
import { getFolderAnalysis, requestFolderAnalysis, updateFolderAnalysis } from '../../services/api'
import { STORAGE_KEYS } from '../../constants/config'
import type { FolderAIAnalysis, ClarifyingQuestion } from '../../types'
import ScoreDisplay from '../shared/ScoreDisplay'

// POST /innovateflow/novelty/folder/{projectId}/{folderId}/clarify — submit
// clarifying answers and receive the refreshed folder analysis. The shared axios
// instance in services/api.ts is module-private and ships no folder-clarify
// wrapper, so this call is composed here with the same auth its request
// interceptor injects (Bearer ID token + active-aggregator header). It targets
// the real deployed endpoint (novelty/handler.py::_clarify_folder) — no stub.
async function submitFolderClarifyingAnswers(
  projectId: string,
  folderId: string,
  answers: ClarifyingQuestion[],
): Promise<FolderAIAnalysis> {
  const endpoint = window.INNOVATEFLOW_CONFIG?.API_ENDPOINT
  const baseURL = endpoint ? `${endpoint}/innovateflow` : ''
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
  if (token) headers.Authorization = `Bearer ${token}`
  const activeAggregatorId = localStorage.getItem('activeAggregatorId')
  if (activeAggregatorId) headers['X-Aggregator-Id'] = activeAggregatorId
  const resp = await axios.post<{ ai_analysis: FolderAIAnalysis }>(
    `${baseURL}/novelty/folder/${projectId}/${folderId}/clarify`,
    { answers },
    { headers },
  )
  return resp.data.ai_analysis
}

interface FolderAIAnalysisPanelProps {
  projectId: string
  folderId: string
}

export default function FolderAIAnalysisPanel({ projectId, folderId }: FolderAIAnalysisPanelProps) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editSummary, setEditSummary] = useState('')
  const [editThemes, setEditThemes] = useState<string[]>([])
  const [editNovelAspects, setEditNovelAspects] = useState<string[]>([])
  const [editOverlapAreas, setEditOverlapAreas] = useState<string[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [clarifyAnswers, setClarifyAnswers] = useState<Record<string, string>>({})

  const { data: analysis, isLoading } = useQuery({
    queryKey: ['folder-analysis', projectId, folderId],
    queryFn: () => getFolderAnalysis(projectId, folderId),
    enabled: !!projectId && !!folderId,
  })

  const generateMutation = useMutation({
    mutationFn: () => requestFolderAnalysis(projectId, folderId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['folder-analysis', projectId, folderId] }),
  })

  const saveMutation = useMutation({
    mutationFn: (corrections: Partial<FolderAIAnalysis>) => updateFolderAnalysis(projectId, folderId, corrections),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-analysis', projectId, folderId] })
      setEditing(false)
    },
  })

  const startEdit = () => {
    if (!analysis) return
    setEditSummary(analysis.summary)
    setEditThemes([...analysis.key_themes])
    setEditNovelAspects([...analysis.novel_aspects])
    setEditOverlapAreas(analysis.overlap_areas.map((a) => (typeof a === 'string' ? a : a?.overlap_description ?? '')).filter(Boolean))
    setEditing(true)
  }

  const saveCorrections = () => {
    saveMutation.mutate({
      summary: editSummary,
      key_themes: editThemes,
      novel_aspects: editNovelAspects,
      overlap_areas: editOverlapAreas,
    })
  }

  const clarifyMutation = useMutation({
    mutationFn: (answers: ClarifyingQuestion[]) => submitFolderClarifyingAnswers(projectId, folderId, answers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-analysis', projectId, folderId] })
      setClarifyAnswers({})
    },
  })

  const submitAnswers = () => {
    if (!analysis?.clarifying_questions) return
    const answered = analysis.clarifying_questions.map((q) => ({
      ...q,
      answer: clarifyAnswers[q.id] || '',
      answered_at: new Date().toISOString(),
    }))
    clarifyMutation.mutate(answered)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    )
  }

  if (!analysis) {
    return (
      <div className="text-center py-8">
        <Sparkles className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <p className="text-sm text-gray-500 mb-4">No AI analysis generated yet for this folder.</p>
        <button
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50"
        >
          {generateMutation.isPending ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Generate AI Analysis</>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ScoreDisplay score={analysis.confidence_score} variant="confidence" />
          <span className="px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded-full">{analysis.domain_classification}</span>
        </div>
        {!editing ? (
          <button onClick={startEdit} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={saveCorrections} disabled={saveMutation.isPending} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50">
              {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Corrections
            </button>
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Summary */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-1">Summary</h4>
        {editing ? (
          <textarea value={editSummary} onChange={(e) => setEditSummary(e.target.value)} className="w-full p-2 text-sm light-input" rows={4} />
        ) : (
          <p className="text-sm text-gray-600">{analysis.summary}</p>
        )}
      </div>

      {/* Key Themes */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-1">Key Themes</h4>
        {editing ? (
          <textarea value={editThemes.join('\n')} onChange={(e) => setEditThemes(e.target.value.split('\n').filter(Boolean))} className="w-full p-2 text-sm light-input" rows={3} placeholder="One theme per line" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {analysis.key_themes.map((theme, i) => (
              <span key={i} className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded-full">{theme}</span>
            ))}
          </div>
        )}
      </div>

      {/* Novel Aspects */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-1">Novel Aspects</h4>
        {editing ? (
          <textarea value={editNovelAspects.join('\n')} onChange={(e) => setEditNovelAspects(e.target.value.split('\n').filter(Boolean))} className="w-full p-2 text-sm light-input" rows={3} placeholder="One aspect per line" />
        ) : (
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
            {analysis.novel_aspects.map((aspect, i) => (
              <li key={i}>{aspect}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Overlap Areas */}
      {analysis.overlap_areas.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Overlap Areas</h4>
          {editing ? (
            <textarea value={editOverlapAreas.join('\n')} onChange={(e) => setEditOverlapAreas(e.target.value.split('\n').filter(Boolean))} className="w-full p-2 text-sm light-input" rows={3} placeholder="One area per line" />
          ) : (
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              {analysis.overlap_areas.map((area, i) => {
                const text = typeof area === 'string' ? area : area?.overlap_description ?? ''
                const severity = typeof area === 'string' ? null : area?.overlap_severity
                if (!text) return null
                return (
                  <li key={i}>
                    {text}
                    {severity ? <span className="ml-2 text-xs text-gray-500">({severity})</span> : null}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}

      {/* Clarifying Questions */}
      {analysis.clarifying_questions.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-medium text-amber-800">Clarifying Questions</h4>
          </div>
          <div className="space-y-3">
            {analysis.clarifying_questions.filter((q: ClarifyingQuestion) => !q.answer).map((q: ClarifyingQuestion) => (
              <div key={q.id}>
                <p className="text-sm text-amber-900 mb-1">{q.question}</p>
                <input
                  type="text"
                  value={clarifyAnswers[q.id] || ''}
                  onChange={(e) => setClarifyAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  placeholder="Your answer..."
                  className="w-full p-2 text-sm text-gray-900 border border-amber-200 rounded focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                />
              </div>
            ))}
          </div>
          <button
            onClick={submitAnswers}
            disabled={clarifyMutation.isPending}
            className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {clarifyMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Submit Answers
          </button>
          {clarifyMutation.isError && (
            <p className="mt-2 text-xs text-red-600">Failed to submit answers. Please try again.</p>
          )}
        </div>
      )}

      {/* Correction History */}
      {analysis.correction_history.length > 0 && (
        <div>
          <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Correction History ({analysis.correction_history.length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {analysis.correction_history.map((entry, i) => (
                <div key={i} className="p-2 bg-gray-50 rounded text-xs text-gray-600">
                  <span className="font-medium">{new Date(entry.timestamp).toLocaleString()}</span>
                  <span className="ml-2">Changed: {entry.changed_fields.join(', ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500">Generated {new Date(analysis.generated_at).toLocaleString()}</p>
    </div>
  )
}
